import React from 'react';
import { useTranslation } from 'react-i18next';
import { LegalPageLayout } from './LegalPageLayout';
import { FaShieldAlt } from 'react-icons/fa';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h2 className="section-title">{children}</h2>;

const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="info-box">{children}</div>
);

const FeatureCard: React.FC<{
  title: string;
  colorClass?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ title, colorClass = 'text-blue-300', children, icon }) => (
  <div className="feature-card">
    <div className="flex items-center gap-2 mb-2">
      {icon && (
        <span className="bg-indigo-500/20 p-1 rounded-full flex-shrink-0">
          {icon}
        </span>
      )}
      <h3 className={`text-lg font-semibold ${colorClass}`}>{title}</h3>
    </div>
    <p className="text-sm text-gray-400">{children}</p>
  </div>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-indigo-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-purple-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    { id: 'intro', title: t('privacyPolicy.sections.intro.title') },
    { id: 'collect', title: t('privacyPolicy.sections.collect.title') },
    { id: 'use', title: t('privacyPolicy.sections.use.title') },
    { id: 'disclosure', title: t('privacyPolicy.sections.disclosure.title') },
    { id: 'security', title: t('privacyPolicy.sections.security.title') },
    { id: 'changes', title: t('privacyPolicy.sections.changes.title') },
  ];

  return (
    <LegalPageLayout
      icon={<FaShieldAlt />}
      title={t('privacyPolicy.title')}
      lastUpdated={t('privacyPolicy.lastUpdated')}
      contentsLabel={t('privacyPolicy.contents')}
      sections={sections}
    >
      {(activeSection) => (
        <div className="animate-fade-in space-y-6">
          {activeSection === 'intro' && (
            <div>
              <SectionTitle>
                {t('privacyPolicy.sections.intro.title')}
              </SectionTitle>
              <InfoBox>
                <p>{t('privacyPolicy.sections.intro.description')}</p>
              </InfoBox>
            </div>
          )}

          {activeSection === 'collect' && (
            <div>
              <SectionTitle>
                {t('privacyPolicy.sections.collect.title')}
              </SectionTitle>
              <p className="text-gray-400 mb-5">
                {t('privacyPolicy.sections.collect.description')}
              </p>
              <div className="space-y-4">
                <FeatureCard
                  title={t('privacyPolicy.sections.collect.personalData')}
                  colorClass="text-blue-300"
                >
                  {t('privacyPolicy.sections.collect.personalDataDesc')}
                </FeatureCard>
                <FeatureCard
                  title={t('privacyPolicy.sections.collect.usageData')}
                  colorClass="text-green-300"
                >
                  {t('privacyPolicy.sections.collect.usageDataDesc')}
                </FeatureCard>
                <FeatureCard
                  title={t('privacyPolicy.sections.collect.cookiesTracking')}
                  colorClass="text-purple-300"
                >
                  {t('privacyPolicy.sections.collect.cookiesTrackingDesc')}
                </FeatureCard>
              </div>
            </div>
          )}

          {activeSection === 'use' && (
            <div>
              <SectionTitle>
                {t('privacyPolicy.sections.use.title')}
              </SectionTitle>
              <p className="text-gray-400 mb-5">
                {t('privacyPolicy.sections.use.description')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  t('privacyPolicy.sections.use.provideService'),
                  t('privacyPolicy.sections.use.notifyChanges'),
                  t('privacyPolicy.sections.use.customerSupport'),
                  t('privacyPolicy.sections.use.gatherAnalysis'),
                  t('privacyPolicy.sections.use.monitorUsage'),
                  t('privacyPolicy.sections.use.technicalIssues'),
                ].map((text, i) => (
                  <div key={i} className="feature-card flex items-start gap-3">
                    <span className="bg-indigo-500/20 p-1 rounded-full flex-shrink-0 mt-0.5">
                      <CheckIcon />
                    </span>
                    <p className="text-sm text-gray-300">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'disclosure' && (
            <div>
              <SectionTitle>
                {t('privacyPolicy.sections.disclosure.title')}
              </SectionTitle>
              <div className="bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg p-4 mb-5">
                <p className="text-sm">
                  {t('privacyPolicy.sections.disclosure.description')}
                </p>
              </div>
              <div className="feature-card">
                <h3 className="text-white font-semibold mb-3">
                  {t('privacyPolicy.sections.disclosure.thirdPartyTitle')}
                </h3>
                <div className="space-y-3">
                  {[
                    t('privacyPolicy.sections.disclosure.serviceProviders'),
                    t('privacyPolicy.sections.disclosure.legalRequirements'),
                    t('privacyPolicy.sections.disclosure.protectionRights'),
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="bg-purple-500/20 p-1 rounded-full flex-shrink-0 mt-0.5">
                        <InfoIcon />
                      </span>
                      <p className="text-sm text-gray-400">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div>
              <SectionTitle>
                {t('privacyPolicy.sections.security.title')}
              </SectionTitle>
              <div className="feature-card">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-green-500/20 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-green-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </span>
                  <h3 className="text-lg font-semibold text-white">
                    {t('privacyPolicy.sections.security.securityMeasures')}
                  </h3>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  {t('privacyPolicy.sections.security.description')}
                </p>
                <div className="warning-box">
                  <h4 className="font-semibold text-yellow-200 text-sm mb-1">
                    {t('privacyPolicy.sections.security.importantNote')}
                  </h4>
                  <p className="text-xs text-gray-400">
                    {t('privacyPolicy.sections.security.noteDescription')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'changes' && (
            <div>
              <SectionTitle>
                {t('privacyPolicy.sections.changes.title')}
              </SectionTitle>
              <p className="text-gray-400 mb-4">
                {t('privacyPolicy.sections.changes.description')}
              </p>
              <div className="feature-card">
                <p className="text-sm text-gray-400">
                  {t('privacyPolicy.sections.changes.reviewEncourage')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </LegalPageLayout>
  );
};
